import { Route } from '@angular/router';
import { KeycloakUserRole } from '@libs/shared/interface/enum';
import { expertWorkspaceResolver } from '@sprint-expert/app/app.resolvers';
import { AuthGuard } from '@sprint-expert/app/core/auth/auth-guard.service';
import { ExpertWorkspaceGuard } from '@sprint-expert/app/core/auth/expert-workspace-guard.service';
import { ExpertWorkspaceTeamPlanGuard } from '@sprint-expert/app/core/auth/expert-workspace-team-plan-guard.service';
import { LayoutComponent } from '@sprint-expert/app/layout/layout.component';

export const appRoutes: Route[] = [
  { path: '', pathMatch: 'full', redirectTo: 'home' },
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'home',
        loadChildren: () =>
          import('@sprint-expert/app/pages/landing/home/home.routes'),
      },
    ],
  },
  {
    path: '',
    canActivate: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'on-boarding',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-on-boarding/expert-on-boarding.routes'
          ),
      },
      {
        path: 'expert-workspace-selection',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-workspace-selection/expert-workspace-selection.routes'
          ),
      },
      {
        path: 'phone-number-verification',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-phone-number-verification/expert-phone-number-verification.routes'
          ),
        canActivate: [AuthGuard],
        data: {
          roles: [KeycloakUserRole.Expert],
        },
      },
      {
        path: 'maintenance',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-system-maintenance/expert-system-maintenance.routes'
          ),
      },
      {
        path: 'expert-program',
        redirectTo: '/',
      },
      {
        path: '500',
        pathMatch: 'full',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/error-internal-server/error-internal-server.routes'
          ),
      },
      {
        path: '404',
        pathMatch: 'full',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/error-not-found/error-not-found.routes'
          ),
      },
    ],
  },
  {
    path: 'expert-workspace/:expertWorkspaceId',
    canActivate: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'without-navigation',
      roles: [KeycloakUserRole.Expert],
    },
    resolve: {
      expertWorkspace: expertWorkspaceResolver,
    },
    children: [
      {
        path: 'expert-workspace-pending-join-inquiry',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-workspace-pending-join-inquiry/expert-workspace-pending-join-inquiry.routes'
          ),
      },
      {
        path: 'expert-workspace-no-active-subscription',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-workspace-no-active-subscription/expert-workspace-no-active-subscription.routes'
          ),
      },
      {
        path: 'expert-workspace-no-team-subscription',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-workspace-no-team-subscription/expert-workspace-no-team-subscription.routes'
          ),
      },
      {
        path: 'expert-workspace-subscription-plan-purchase',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-workspace-subscription-plan-purchase/expert-workspace-subscription-plan-purchase.routes'
          ),
      },
      {
        path: 'expert-workspace-subscription-plan-purchase-checkout',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-workspace-subscription-plan-purchase-checkout/expert-workspace-subscription-plan-purchase-checkout.routes'
          ),
      },
      {
        path: 'attendance-kiosk',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-member-attendance-kiosk/expert-member-attendance-kiosk.routes'
          ),
      },
    ],
  },

  // Landing routes
  {
    path: 'expert-workspace/:expertWorkspaceId',
    canActivate: [AuthGuard, ExpertWorkspaceGuard],
    component: LayoutComponent,
    data: {
      layout: 'with-navigation',
      roles: [KeycloakUserRole.Expert],
    },
    children: [
      {
        path: 'expert-member',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-member-list/expert-member-list.routes'
          ),
      },
      {
        path: 'expert-schedule',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-schedule/expert-schedule.routes'
          ),
      },
      {
        path: 'expert-member-chat',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-member-chat/expert-member-chat.routes'
          ),
      },
      {
        path: 'expert-member-pass-template',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-member-pass-template-list/expert-member-pass-template-list.routes'
          ),
      },
      {
        path: 'expert-member-survey-template',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-member-survey-template-list/expert-member-survey-template-list.routes'
          ),
      },
      {
        path: 'expert-member-tag',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-member-tag-list/expert-member-tag-list.routes'
          ),
      },
      {
        path: 'workout-template',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/routine-template-list/routine-template-list.routes'
          ),
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('@sprint-expert/app/pages/dashboard/dashboard.routes'),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-settings/expert-settings.routes'
          ),
      },
      {
        path: 'expert-workspace-subscription-plan-change-checkout',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/single/expert-workspace-subscription-plan-change-checkout/expert-workspace-subscription-plan-change-checkout.routes'
          ),
      },

      {
        path: '**',
        redirectTo: '404',
      },
    ],
  },
  {
    path: 'expert-workspace/:expertWorkspaceId/team',
    canActivate: [AuthGuard, ExpertWorkspaceTeamPlanGuard],
    component: LayoutComponent,
    data: {
      layout: 'with-navigation',
      roles: [KeycloakUserRole.Expert],
    },
    children: [
      {
        path: 'expert-workspace-schedule',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-workspace-schedule/expert-workspace-schedule.routes'
          ),
      },
      {
        path: 'expert-workspace-expert',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-workspace-expert-list/expert-workspace-expert-list.routes'
          ),
      },
      {
        path: 'expert-workspace-member',
        loadChildren: () =>
          import(
            '@sprint-expert/app/pages/expert-workspace-member-list/expert-workspace-member-list.routes'
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
